package com.taocent.simple.compose.component.richtext.core.internal.undo

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlin.time.TimeSource

/**
 * Undo 栈核心:管理 undo/redo 双端队列、合并窗口、容量上限、可观察状态。
 *
 * 业务侧 (文本级 `UndoManager` / 块级 `BlockUndoManager`) 只需要关心
 * 自己的 Entry 类型与合并策略,把栈/时间/容量/canUndo/canRedo 这些
 * 不变式委托给本类。
 *
 * **注意:** 此类型在源代码层级属于 `internal` 命名空间,
 * 但因为 KMP `internal` 不跨模块(`richtext-core` ↔ `blockrichtext`),
 * 必须声明为 `public` 才能被 `BlockUndoManager` 复用。
 * 库外代码不应该直接构造或依赖本类型,后续可能拆为 `expect/actual`
 * 或在 API 表面做更严格的可见性控制。
 *
 * ## 合并策略契约
 * 业务侧调用 [isInMergeWindow] 判断是否处于合并窗口期(可合并)。
 * 如果处于窗口期,业务侧通过 [peekUndo] / [replaceUndoTop] 完成合并。
 * 否则调用 [pushUndo] 推入新 Entry。
 *
 * ## undo/redo 行为契约
 * - [popLastUndo] 取出 undo 栈顶(不联动 redo),业务侧自己 pushRedo。
 * - [popLastRedo] 取出 redo 栈顶(不联动 undo),业务侧自己 pushUndo(clearRedo=false)。
 * - 业务侧按自己 Entry 的语义把出栈结果转换成返回值。
 */
class UndoStackCore<E>(
    private val maxSize: Int = 100,
    private val mergeIntervalMs: Long = 500
) {
    private val undoStack = ArrayDeque<E>()
    private val redoStack = ArrayDeque<E>()
    private var lastRecordMark = TimeSource.Monotonic.markNow()
    private var preventMerge = false

    var canUndo by mutableStateOf(false)
        private set

    var canRedo by mutableStateOf(false)
        private set

    fun updateAvailability() {
        canUndo = undoStack.isNotEmpty()
        canRedo = redoStack.isNotEmpty()
    }

    /**
     * 当前是否处于可合并窗口。
     * 合并窗口 = `!preventMerge && undoStack 非空 && 与上一条 record 的间隔 < mergeIntervalMs`。
     */
    fun isInMergeWindow(): Boolean {
        if (preventMerge) return false
        if (undoStack.isEmpty()) return false
        val now = TimeSource.Monotonic.markNow()
        return (now - lastRecordMark).inWholeMilliseconds < mergeIntervalMs
    }

    /**
     * 查看 undo 栈顶(不弹出)。栈空返回 null。
     */
    fun peekUndo(): E? = undoStack.lastOrNull()

    /**
     * 替换 undo 栈顶并刷新合并时间戳。
     * 用于合并窗口命中时把"旧栈顶 + 新 entry"合并后写回。
     */
    fun replaceUndoTop(newEntry: E) {
        undoStack.removeLast()
        undoStack.addLast(newEntry)
        lastRecordMark = TimeSource.Monotonic.markNow()
        updateAvailability()
    }

    /**
     * 把新 entry 推入 undo 栈。
     *
     * 默认行为(=正常 record 路径):
     * - 容量溢出时丢弃最早的 entry
     * - 清空 redo 栈(新动作使 redo 失效)
     * - 重置 `lastRecordMark` 和 `preventMerge`
     *
     * 特殊场景:redo 路径中也需要把 entry 推入 undo 栈,但不应该清空 redo 栈
     * (否则 redo 路径会被自身的回退破坏)。这种场景传 `clearRedo = false`。
     */
    fun pushUndo(entry: E, clearRedo: Boolean = true) {
        undoStack.addLast(entry)
        if (undoStack.size > maxSize) undoStack.removeFirst()
        if (clearRedo) redoStack.clear()
        lastRecordMark = TimeSource.Monotonic.markNow()
        preventMerge = false
        updateAvailability()
    }

    /**
     * 把 entry 推入 redo 栈(undo 路径的副产品)。
     * 置 `preventMerge = true`,这样后续 record 不会与刚撤销的 entry 合并。
     */
    fun pushRedo(entry: E) {
        redoStack.addLast(entry)
        preventMerge = true
        updateAvailability()
    }

    /**
     * 弹出 undo 栈顶(不联动 redo),栈空返回 null。
     * 业务侧拿到 entry 后,自己构造 redo entry 并 [pushRedo]。
     */
    fun popLastUndo(): E? {
        if (undoStack.isEmpty()) return null
        val entry = undoStack.removeLast()
        updateAvailability()
        return entry
    }

    /**
     * 弹出 redo 栈顶(不联动 undo),栈空返回 null。
     */
    fun popLastRedo(): E? {
        if (redoStack.isEmpty()) return null
        val entry = redoStack.removeLast()
        updateAvailability()
        return entry
    }

    /**
     * 查看 redo 栈顶(不弹出)。用于"redo 前 peek 看是否要做额外处理"。
     */
    fun peekRedo(): E? = redoStack.lastOrNull()

    fun clear() {
        undoStack.clear()
        redoStack.clear()
        lastRecordMark = TimeSource.Monotonic.markNow()
        preventMerge = false
        updateAvailability()
    }
}
